import {useEffect, useRef, useState} from 'react';
import {ACCOUNT_ID, getAccountStatus} from '../../api/account';
import {getPlans} from '../../api/plans';
import {createSubscription, createTopup} from '../../api/payment';
import {getYandexClientId, trackEvent} from '../../utils/analytics';
import {showError} from '../../utils/notify';

// Мини-пейвол поверх ночного плеера: открывается, когда /unlock вернул 402
// (нет кредитов). Без подписки продаёт месячную подписку, с активной подпиской
// — разовые пакеты. После оплаты вызывает onPaid — SkazkiCard сам повторяет
// unlock.

const TIER_LABELS = {lite: 'Личный', pro: 'Семейный'};

const REVIEWS = [
    {
        name: 'Анна К.',
        text: '«Спасение перед сном. Сын каждый вечер просит сказку и засыпает на середине».',
    },
    {
        name: 'Дмитрий Р.',
        text: '«Сделали сказку про нашу Машу — она теперь героиня собственной истории. Восторг!»',
    },
    {
        name: 'Мария П.',
        text: '«Послушали отрывок — сразу оформили. Голос приятный, дочка слушает не отрываясь».',
    },
];

function tierKey(plan) {
    return (plan.id || '').includes('pro') ? 'pro' : 'lite';
}

function pluralTales(n) {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m10 === 1 && m100 !== 11) return 'сказка';
    if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return 'сказки';
    return 'сказок';
}

export default function SkazkiUnlockSheet({onClose, onPaid}) {
    const [plans, setPlans] = useState(null);          // {subscriptions, topups} | null пока грузится
    const [hasSubscription, setHasSubscription] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [paying, setPaying] = useState(false);
    const [widgetOpen, setWidgetOpen] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const widgetRef = useRef(null);

    useEffect(() => {
        trackEvent('see_paywall_skazki_sheet');
        let cancelled = false;
        Promise.all([getPlans('skazki'), getAccountStatus().catch(() => null)])
            .then(([planData, status]) => {
                if (cancelled) return;
                const sub = status?.subscription;
                setHasSubscription(
                    !!sub && ['active', 'cancel_at_period_end', 'failed'].includes(sub.status));
                setPlans({
                    subscriptions: (planData.subscriptions || [])
                        .filter(p => p.billing_period === 'monthly'),
                    topups: planData.topups || [],
                });
            })
            .catch(() => {
                if (!cancelled) setLoadError(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const offers = plans
        ? (hasSubscription && plans.topups.length ? plans.topups : plans.subscriptions)
        : [];
    const isTopupMode = !!plans && hasSubscription && plans.topups.length > 0;

    useEffect(() => {
        if (selectedId || !offers.length) return;
        const preferred = isTopupMode
            ? (offers.find(p => p.id === 'plan_3') || offers[0])
            : (offers.find(p => tierKey(p) === 'pro') || offers[0]);
        setSelectedId(preferred.id);
    }, [offers, selectedId, isTopupMode]);

    const openWidget = (confirmationToken) => {
        setWidgetOpen(true);
        setTimeout(() => {
            if (!widgetRef.current || !window.YooMoneyCheckoutWidget) {
                showError('Не удалось открыть оплату. Попробуйте ещё раз.');
                setWidgetOpen(false);
                setPaying(false);
                return;
            }
            const checkout = new window.YooMoneyCheckoutWidget({
                confirmation_token: confirmationToken,
                error_callback: () => {
                    showError('Ошибка оплаты. Попробуйте ещё раз.');
                    setWidgetOpen(false);
                    setPaying(false);
                },
            });
            checkout.on('success', () => {
                trackEvent('skazki_unlock_paid');
                setPaying(false);
                onPaid?.();
            });
            checkout.on('fail', () => {
                showError('Оплата не прошла. Попробуйте ещё раз.');
                setWidgetOpen(false);
                setPaying(false);
            });
            checkout.render('sk-unlock-yookassa');
        }, 100);
    };

    const handlePay = async () => {
        const plan = offers.find(p => p.id === selectedId);
        if (!plan || paying) return;
        trackEvent(isTopupMode ? 'start_topup' : 'start_subscription');
        setPaying(true);
        try {
            const yandexClientId = await getYandexClientId();
            const order = isTopupMode
                ? await createTopup({accountId: ACCOUNT_ID, planId: plan.id, yandexClientId})
                : await createSubscription({accountId: ACCOUNT_ID, planId: plan.id, yandexClientId});
            if (!order?.confirmation_token) throw new Error('no confirmation token');
            openWidget(order.confirmation_token);
        } catch (e) {
            console.error('unlock pay error:', e);
            showError(e?.data?.detail || 'Не удалось открыть оплату. Попробуйте ещё раз.');
            setPaying(false);
        }
    };

    const selected = offers.find(p => p.id === selectedId);

    return (
        <div
            className="fixed inset-0 z-50 bg-[#0B0F28]/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={onClose}
        >
            <div
                className="relative w-full sm:max-w-[620px] bg-[#F7F1E3] text-[#221D2D] rounded-t-[22px] sm:rounded-[22px] px-5 py-6 sm:p-7 shadow-2xl max-h-[92dvh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button" onClick={onClose} aria-label="Закрыть"
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#221D2D]/8 flex items-center justify-center text-[#221D2D]/60 hover:bg-[#221D2D]/15 transition"
                >
                    <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M1 1l12 12M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                </button>

                <div className="sk-night-card-eyebrow">Сказка ждёт</div>
                <h2 className="mt-2 text-[22px] sm:text-[24px] font-bold leading-[1.15] tracking-[-0.01em]">
                    Откройте сказку целиком
                </h2>
                <p className="mt-1.5 text-[14px] text-[#221D2D]/65 leading-snug">
                    {isTopupMode
                        ? 'Сказки в подписке закончились — добавьте разовый пакет, и эта сказка откроется сразу.'
                        : 'Подписка сразу откроет эту сказку — и новые каждый месяц.'}
                </p>

                {loadError && (
                    <p className="mt-5 text-[14px] text-error">
                        Не удалось загрузить тарифы. Обновите страницу и попробуйте ещё раз.
                    </p>
                )}

                {!plans && !loadError && (
                    <div className="mt-6 flex justify-center py-6">
                        <span
                            className="inline-block w-7 h-7 border-2 border-[#221D2D]/15 border-t-[#E8804F] rounded-full animate-spin"/>
                    </div>
                )}

                {widgetOpen ? (
                    <div id="sk-unlock-yookassa" ref={widgetRef} className="mt-5"/>
                ) : plans && !loadError && (
                    <>
                        <div className="mt-5 flex flex-col gap-2.5">
                            {offers.map(plan => {
                                const on = plan.id === selectedId;
                                const tales = plan.skazki_credits || 0;
                                const name = isTopupMode
                                    ? `${tales} ${pluralTales(tales)}`
                                    : (TIER_LABELS[tierKey(plan)] || plan.name);
                                const caption = isTopupMode
                                    ? 'разовый пакет, не сгорает'
                                    : `${tales} ${pluralTales(tales)} в месяц`;
                                return (
                                    <button
                                        key={plan.id} type="button"
                                        onClick={() => setSelectedId(plan.id)}
                                        aria-pressed={on}
                                        className={`flex items-center justify-between gap-3 rounded-[14px] border-2 px-4 py-3 text-left transition ${
                                            on ? 'border-[#E8804F] bg-[#E8804F]/10' : 'border-[#221D2D]/12 hover:border-[#221D2D]/30'
                                        }`}
                                    >
                                        <span>
                                            <span className="block text-[15px] font-bold">{name}</span>
                                            <span className="block text-[12px] text-[#221D2D]/55">{caption}</span>
                                        </span>
                                        <span className="text-right shrink-0">
                                            <span className="block text-[17px] font-bold tabular-nums">
                                                {plan.price_rub?.toLocaleString('ru-RU')} ₽
                                            </span>
                                            {!isTopupMode && (
                                                <span className="block text-[11px] text-[#221D2D]/50">в месяц</span>
                                            )}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            type="button"
                            onClick={handlePay}
                            disabled={paying || !selected}
                            className="mt-5 w-full h-[54px] rounded-full bg-[#E8804F] text-[#F7F1E3] text-[16px] font-bold shadow-[0_10px_26px_rgba(232,128,79,0.4)] hover:opacity-95 active:translate-y-[1px] transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
                        >
                            {paying ? (
                                <>
                                    <span
                                        className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"/>
                                    Открываем оплату…
                                </>
                            ) : (
                                'Оплатить и открыть сказку'
                            )}
                        </button>

                        <p className="mt-3 text-center text-[12px] text-[#221D2D]/50">
                            🔒 СБП и карты · ЮКасса
                        </p>

                        {/* Отзывы родителей + строка доверия (как в макете) */}
                        <div className="mt-6">
                            <div className="sk-night-card-eyebrow">Что говорят родители</div>
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                {REVIEWS.map(review => (
                                    <figure
                                        key={review.name}
                                        className="rounded-[14px] border border-[#221D2D]/15 bg-[#F1E9D8]/70 px-3.5 pt-3 pb-3.5 flex flex-col"
                                    >
                                        <div className="text-[#E8804F] text-[12px] tracking-[0.22em]"
                                             role="img" aria-label="Оценка 5 из 5">
                                            ★★★★★
                                        </div>
                                        <blockquote className="mt-1.5 text-[13px] leading-[1.4] text-[#221D2D]">
                                            {review.text}
                                        </blockquote>
                                        <figcaption
                                            className="mt-auto pt-3 flex items-center gap-2 text-[12px] text-[#221D2D]/55">
                                            <span aria-hidden="true"
                                                  className="w-6 h-6 rounded-full border-[1.5px] border-[#221D2D]/35 flex-shrink-0"/>
                                            {review.name}
                                        </figcaption>
                                    </figure>
                                ))}
                            </div>
                        </div>

                        <div
                            className="mt-5 pt-4 border-t border-dashed border-[#221D2D]/25 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[12px] text-[#221D2D]/60 text-center">
                            <span>
                                <span className="text-[#E8804F]">★</span>{' '}
                                <b className="text-[#221D2D]/80">4.8</b> средняя оценка
                            </span>
                            <span className="hidden sm:inline text-[#221D2D]/25">|</span>
                            <span>
                                <b className="text-[#221D2D]/80">12 000+</b> семей укладывают спать со сказками
                            </span>
                            <span className="hidden sm:inline text-[#221D2D]/25">|</span>
                            <span>Отмена в любой момент</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
